import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileWarning, Loader2, X } from 'lucide-react';
import { fetchPreviewBlob, ApiCallError, type PreviewTarget } from '@/api/client';

/**
 * LOOKING AT A SCAN WITHOUT LEAVING THE SCHEDULE.
 *
 * ---- ⭐ WHY IT IS A PANEL AND NOT A TAB ------------------------------------
 *
 * Pete, 2026-08-25: *"can we do the document viewer as a popup next to the paperwork
 * instead of a new tab?"*
 *
 * ⛔ AND HE IS RIGHT FOR A REASON BEYOND TASTE. The question a preview answers is
 * "which load is this for", and the answer is ON THE SCHEDULE — the arrivals, the
 * doors, the times. A new tab hides the one thing you need to compare the document
 * against, and getting back means finding the old tab. The document has to be beside
 * the schedule, not instead of it.
 *
 * ---- ⭐ WHY IT IS A POPUP AND NOT A MODAL ----------------------------------
 *
 * ⛔ NOTHING IS BLOCKED WHILE THIS IS OPEN. There is no overlay and no focus trap: a
 * scheduler can look at the scan and drag the next one, or answer the phone and click
 * a door. A modal would make "look at it, then file it" two separate acts.
 *
 * ⚠️ IT IS PORTALLED TO THE BODY, deliberately. The tray lives inside a column that
 * scrolls and clips; a panel rendered in place would be cut off at the column edge,
 * which is the one place it must not be.
 *
 * ---- ⛔ THE BYTES COME FROM OUR OWN ROUTE, AND THAT IS LOAD-BEARING ---------
 *
 * Not the storage SAS and not the Graph download URL. Both are stamped `attachment`
 * (so a frame pointed at either DOWNLOADS the file instead of drawing it) and neither
 * origin grants CORS to this app (so it could not be read back anyway). See
 * lib/previewTypes.ts on the server for what is allowed to render inline and why SVG
 * is not on the list.
 */

/** ⚠️ Object URLs are copies held in memory. Exactly one is alive at a time. */
interface Loaded {
    url: string;
    contentType: string;
}

type State =
    | { kind: 'Loading' }
    | { kind: 'Loaded'; data: Loaded }
    | { kind: 'CannotDraw'; message: string }
    | { kind: 'Failed'; message: string };

interface Props {
    /** The tray. The panel is placed beside it, and follows it as the page moves. */
    anchor: HTMLElement | null;
    /** Null closes the panel. */
    target: PreviewTarget | null;
    /** The file name, for the header and for the download fallback. */
    name: string;
    getToken: () => Promise<string>;
    onClose: () => void;
    /**
     * ⭐ THE ESCAPE HATCH, AND IT IS NOT AN AFTERTHOUGHT. A .docx or a .tif is real
     * paperwork this viewer cannot draw; the panel offers the old open-in-a-tab path
     * rather than pretending the file is broken.
     */
    onDownload: () => void;
}

const PANEL_W = 520;
const GAP = 12;

export function DocumentPreview({ anchor, target, name, getToken, onClose, onDownload }: Props) {
    const [state, setState] = useState<State>({ kind: 'Loading' });
    const [box, setBox] = useState<{ left: number; top: number; height: number } | null>(null);
    /** ⚠️ Held in a ref as well as in state so cleanup can revoke without re-running. */
    const urlRef = useRef<string | null>(null);

    const revoke = useCallback(() => {
        if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        }
    }, []);

    /**
     * ---- ⭐ PLACED BESIDE THE TRAY, NOT AT A FIXED CORNER ----------------------
     *
     * ⚠️ MEASURED, AND RE-MEASURED. The arrivals column is not always the same width
     * — it is not the same width on a laptop as on the dock office's monitor — and the
     * page scrolls underneath. A hard-coded `right: 380px` is right on one machine.
     *
     * ⛔ AND IT FLIPS TO THE OTHER SIDE RATHER THAN GOING OFF-SCREEN. On a narrow
     * window there is not 520px to the left of the tray; the panel then sits at the
     * left edge instead. A viewer half off the screen is worse than a tab.
     */
    const place = useCallback(() => {
        if (!anchor) return;
        const r = anchor.getBoundingClientRect();
        const wanted = r.left - PANEL_W - GAP;
        const left = wanted >= GAP ? wanted : GAP;
        // Top-aligned with the tray, but never pushed off the bottom of a short window.
        const height = Math.min(window.innerHeight - 2 * GAP, 640);
        const top = Math.max(GAP, Math.min(r.top, window.innerHeight - height - GAP));
        setBox({ left, top, height });
    }, [anchor]);

    useLayoutEffect(() => {
        if (!target) return;
        place();
        window.addEventListener('resize', place);
        // ⚠️ Capture phase: the column scrolls, not the window, and a scroll event on
        // an inner element does not bubble to window without it.
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [target, place]);

    /**
     * ⭐ Escape closes it. The one keyboard convention a panel like this must keep.
     *
     * ⛔⛔ CAPTURE PHASE, AND IT SWALLOWS THE EVENT (2026-08-27). Pete, twice:
     * *"When I 'X' out for the .pdf in Paperwork, both popups close."*
     *
     * ⚠️ THE PARENT DIALOGS ALREADY GUARD THEMSELVES — `AppointmentDocumentsDialog`
     * and `EditAppointmentDialog` both carry `onEscapeKeyDown` handlers that check
     * for this panel. But that is a rule EVERY future parent has to remember, and
     * the failure when one forgets is silent: the dialog underneath simply vanishes
     * and the scheduler loses whatever they had typed in it.
     *
     * ⭐ SO THE PANEL NOW DEFENDS ITSELF. Listening on the capture phase and calling
     * `stopPropagation` means Radix never sees the key at all — the guard stops
     * being a thing three parents have to opt into and becomes a property of the
     * preview. ⚠️ The parents' handlers stay: belt and braces on a bug reported twice.
     */
    useEffect(() => {
        if (!target) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            e.preventDefault();
            onClose();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [target, onClose]);

    /**
     * ⛔ AND THE SAME FOR POINTER EVENTS, FOR THE SAME REASON. Radix tears a dialog
     * down on a pointerdown outside its own subtree, and this panel portals itself
     * to `document.body` — so it is ALWAYS outside. The `data-document-preview` mark
     * lets a parent recognise it, but again only if the parent remembers to look.
     *
     * ⚠️ STOPPED AT CAPTURE, BEFORE RADIX'S DOCUMENT-LEVEL LISTENER RUNS. The panel's
     * own buttons still work: `stopPropagation` halts the bubble to Radix, it does
     * not cancel the click on the element under the pointer.
     */
    useEffect(() => {
        if (!target) return;
        const node = () => document.querySelector('[data-document-preview]');
        const swallow = (e: Event) => {
            const el = node();
            if (el && e.target instanceof Node && el.contains(e.target)) e.stopPropagation();
        };
        for (const type of ['pointerdown', 'mousedown', 'touchstart'] as const) {
            document.addEventListener(type, swallow, true);
        }
        return () => {
            for (const type of ['pointerdown', 'mousedown', 'touchstart'] as const) {
                document.removeEventListener(type, swallow, true);
            }
        };
    }, [target]);

    useEffect(() => {
        if (!target) { revoke(); return; }

        let cancelled = false;
        setState({ kind: 'Loading' });
        revoke();

        void (async () => {
            try {
                const got = await fetchPreviewBlob(await getToken(), target);
                /**
                 * ⚠️ A RACE THAT COSTS MEMORY, NOT CORRECTNESS — and is still worth
                 * closing. Clicking three scans quickly starts three fetches; the two
                 * that lose still created an object URL, and nothing else would ever
                 * revoke them.
                 */
                if (cancelled) { URL.revokeObjectURL(got.url); return; }
                urlRef.current = got.url;
                setState({ kind: 'Loaded', data: got });
            } catch (err) {
                if (cancelled) return;
                if (err instanceof ApiCallError
                    && (err.details.code === 'Unpreviewable' || err.details.code === 'TooBig')) {
                    setState({ kind: 'CannotDraw', message: err.details.error });
                } else {
                    setState({
                        kind: 'Failed',
                        message: err instanceof ApiCallError
                            ? err.details.error
                            : 'Could not open that file.',
                    });
                }
            }
        })();

        return () => { cancelled = true; };
    }, [target, getToken, revoke]);

    // ⚠️ Unmount must revoke too — closing the tab is not the only way this goes away.
    useEffect(() => revoke, [revoke]);

    if (!target || !box) return null;

    return createPortal(
        <div
            /**
             * ⛔ THE MARK A PARENT DIALOG LOOKS FOR, AND THE WHOLE POINT OF IT.
             *
             * ⚠️ This panel portals itself to document.body, so it lives OUTSIDE the
             * Radix DialogContent that opened it. Radix closes a dialog on any
             * pointer event outside its own subtree — so scrolling this preview, or
             * pressing its X, registered as "the user clicked away" and tore down the
             * dialog underneath. Pete, 2026-08-25: *"When I scroll on the new dock to
             * see the signature, everything closes... when I 'x' out of the pdf, it
             * doesn't go back to the previous popup, it closes both."*
             *
             * ⭐ A DATA ATTRIBUTE RATHER THAN A REF, deliberately: three different
             * screens mount this panel, and a ref would have to be threaded through
             * every one of them. The parent asks `closest('[data-document-preview]')`
             * and needs to know nothing else.
             */
            data-document-preview=""
            role="dialog"
            aria-label={`Preview of ${name}`}
            style={{ left: box.left, top: box.top, width: PANEL_W, height: box.height }}
            className="fixed z-50 flex flex-col overflow-hidden rounded-lg border bg-background shadow-2xl"
        >
            <div className="flex items-center gap-2 border-b px-3 py-2">
                <span className="min-w-0 flex-1 truncate font-mono text-xs" title={name}>{name}</span>
                <button
                    type="button"
                    onClick={onDownload}
                    title="Download it, or open it in a new tab"
                    aria-label="Download"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                    <Download className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    title="Close (Esc)"
                    aria-label="Close preview"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            <div className="flex-1 overflow-auto bg-muted/30">
                {state.kind === 'Loading' && (
                    <div className="flex h-full items-center justify-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Fetching…
                    </div>
                )}

                {state.kind === 'Loaded' && (
                    state.data.contentType === 'application/pdf'
                        /**
                         * ⚠️ AN IFRAME FOR PDF, AN <img> FOR EVERYTHING ELSE. The
                         * browser's PDF viewer brings its own scrolling and zoom, which
                         * is most of what makes a bill of lading readable in 520px; a
                         * raster image in an iframe would get none of that and would
                         * arrive centred on a grey field it cannot be scrolled around.
                         *
                         * ⛔ The iframe is only ever pointed at an object URL built from
                         * bytes this app fetched and the server allowlisted. It is never
                         * pointed at a URL that came back from an API.
                         */
                        ? <iframe
                            src={state.data.url}
                            title={name}
                            className="h-full w-full border-0"
                        />
                        : <div className="flex min-h-full items-start justify-center p-2">
                            <img src={state.data.url} alt={name} className="max-w-full" />
                        </div>
                )}

                {(state.kind === 'CannotDraw' || state.kind === 'Failed') && (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                        <FileWarning className={'h-6 w-6 '
                            + (state.kind === 'Failed' ? 'text-destructive' : 'text-muted-foreground')} />
                        <p className="text-xs text-muted-foreground">{state.message}</p>
                        {/**
                          * ⭐ THE WAY OUT IS ON THE SCREEN THAT SAYS NO. "Cannot preview"
                          * with no next step is how a scheduler concludes the file is
                          * broken and goes looking for the person who scanned it.
                          */}
                        <button
                            type="button"
                            onClick={onDownload}
                            className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs
                                       hover:border-primary/50 hover:bg-muted"
                        >
                            <Download className="h-3.5 w-3.5" /> Open it instead
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}
